import { DataError } from "../../../../core/errors/DataError";
import {
  BaseLogo,
  LogoState,
  LogoType,
  getLogoState,
} from "../../../../core/models/entities/BaseLogo";
import {
  CustomerLogoLink,
  CustomerLogoLinkSource,
} from "../../../../core/models/entities/CustomerLogoLink";
import { CustomerLogoAttachRejectedDto } from "../../../../data/models/responses/CustomerLogoAttachResultDto";
import { enviroment } from "../../../../enviroment";
import { BadgeTone } from "../../../components/OrgShell/BadgeChip";
import { SortDirection } from "../../../components/GenericTable/Models/sortDirection";
import { organizationFilterField } from "../../../shared/const/organizationFilterField";
import { PageQuery } from "../../../../utils/models/pageQuery";
import { formatDate } from "../../../../utils/functions/DateUtil";

/**
 * Pure helpers for the customer edit screen's Logos section (flag
 * REACT_APP_CUSTOMER_LOGOS_TAB_ENABLED). No React, no I/O - unit-tested on
 * their own.
 */

/** A search fires only for a term of this many characters (trimmed). */
export const CUSTOMER_LOGO_SEARCH_MIN_CHARS = 2;
/** Keystrokes are coalesced for this long before ONE GET leaves. */
export const CUSTOMER_LOGO_SEARCH_DEBOUNCE_MS = 400;
/** Page size of the customer's own logo list (flag fast off = today). */
export const CUSTOMER_LOGOS_PAGE_SIZE = 50;
/**
 * Page size with the flag REACT_APP_CUSTOMER_LOGOS_TAB_FAST_ENABLED: half
 * the rows to hydrate and price per page (the pager is the same).
 */
export const CUSTOMER_LOGOS_FAST_PAGE_SIZE = 25;
/** The search shows its first page only (the term narrows it down). */
export const CUSTOMER_LOGO_SEARCH_PAGE_SIZE = 25;
/**
 * The idle delay of the logos prefetch (flag fast): well past the edit
 * screen's first paint, long before a typical section click - the same
 * delay the Pricing prefetch uses.
 */
export const CUSTOMER_LOGOS_PREFETCH_IDLE_MS = 800;
/**
 * staleTime of the customer's page and links under the flag fast - written
 * by the prefetch AND read by the section's two hooks (they must agree):
 * react-query refetches a cached entry older than the hook's staleTime the
 * moment its query becomes enabled (shouldFetchOptionally -> isStaleByTime),
 * so with today's 60 s a click later than a minute after the prefetch would
 * cost the same 2 GET again (the prefetch spent). 5 min covers the typical
 * "edit the details, then open Logos" visit; mutations invalidate the two
 * queries explicitly (invalidateCustomerLogoQueries) and the table shows no
 * prices, so nothing else needs the entries younger. Fast off = 60 s.
 */
export const CUSTOMER_LOGOS_FAST_STALE_MS = 300_000;

/** Newest first - the order of the customer's list and of the search. */
export const CUSTOMER_LOGOS_SORT_NEWEST_FIRST: PageQuery["sortBy"] = {
  field: "updatedAtUtc",
  sortDirection: SortDirection.DESC,
};

/** True when the logo tab fast flag is on (evaluated per call: tests flip it). */
export function isCustomerLogosTabFast(): boolean {
  return enviroment.customerLogosTabFastEnabled === true;
}

/** The customer list's page size: 25 with the fast flag, 50 without. */
export function customerLogosPageSize(): number {
  return isCustomerLogosTabFast()
    ? CUSTOMER_LOGOS_FAST_PAGE_SIZE
    : CUSTOMER_LOGOS_PAGE_SIZE;
}

/**
 * The PageQuery of the customer's own logo page - the ONE builder both the
 * section's hook (useAllLogos) and the prefetch (prefetchLogos) use, so
 * toQueryParam yields the identical query string and therefore the identical
 * react-query key ["logos", actAs, undefined, qs]. Two hand-built queries
 * that differ in one field would miss the cache and cost a third GET.
 */
export function customerLogosPageQuery(
  customerId: number,
  pageIndex: number
): PageQuery {
  return {
    searchTerm: "",
    filterBy: [{ field: organizationFilterField, value: String(customerId) }],
    sortBy: CUSTOMER_LOGOS_SORT_NEWEST_FIRST,
    page: { size: customerLogosPageSize(), index: pageIndex },
  };
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function isSearchTermReady(term: string): boolean {
  return term.trim().length >= CUSTOMER_LOGO_SEARCH_MIN_CHARS;
}

/**
 * Why the manual-link data (GET organizations/{orgId}/logos/attach) is not
 * available - drives the note that replaces the add/remove UI:
 *  - "unavailable": 404 WITHOUT a message = the route is not there (the API
 *                   flag CustomerLogoAttachEnabled is off: NotFoundResult,
 *                   empty body). This is the note the prod roll-out reads.
 *  - "forbidden":   403 = the policy refused (not the customer's direct
 *                   dealer, vendor org, customer without a vendor), or the
 *                   auth layer's 400 for a caller outside the customer's org
 *                   tree / without the vendor role (WithOrganizationAccess
 *                   answers BadRequestException there, like every Role.Vendor
 *                   route).
 *  - "error":       anything else - including a 404 WITH a message, which is
 *                   the policy's "Customer organization not found." (an
 *                   unknown or deleted org), never the flag.
 */
export type AttachBlockedReason = "unavailable" | "forbidden" | "error";

const AUTH_LAYER_400 = /does not have (access to requested organization|required role)/i;

export function attachBlockedReason(
  error: unknown
): AttachBlockedReason | undefined {
  if (!error) return undefined;
  const status = error instanceof DataError ? error.errorStatus : undefined;
  const code = status?.code;
  const message = typeof status?.message === "string" ? status.message.trim() : "";
  if (code === 404) return message ? "error" : "unavailable";
  if (code === 403) return "forbidden";
  if (code === 400 && AUTH_LAYER_400.test(message)) return "forbidden";
  return "error";
}

/**
 * The API's own words for a blocked/failed call, when it sent any (the
 * policy's 404/403 messages, the auth layer's 400); "" for the flag-off 404
 * (empty body) and for anything that is not a DataError.
 */
export function attachBlockedMessage(error: unknown): string {
  if (!(error instanceof DataError)) return "";
  const message = error.errorStatus?.message;
  return typeof message === "string" ? message.trim() : "";
}

const REJECT_REASON_I18N_KEYS: Record<string, string> = {
  notFound: "customerUpdate.logosRejectedNotFound",
  notVendorLogo: "customerUpdate.logosRejectedNotVendorLogo",
};

/**
 * "{{count}} logo(s) were skipped: not one of your logos" - one info toast
 * for the rejected part of an attach answer; undefined when nothing was
 * skipped. The reason codes are data values from the API, mapped to i18n
 * keys here (an unknown code is shown as it is).
 */
export function attachRejectedText(
  rejected: CustomerLogoAttachRejectedDto[] | undefined,
  t: Translate
): string | undefined {
  if (!rejected || rejected.length === 0) return undefined;
  const reasons = Array.from(new Set(rejected.map((r) => r.reason))).map(
    (reason) => {
      const key = REJECT_REASON_I18N_KEYS[reason];
      return key ? t(key) : reason;
    }
  );
  return t("customerUpdate.logosRejected", {
    count: rejected.length,
    reasons: reasons.join(", "),
  });
}

/**
 * Error toast text of a failed attach: the API's scope answers get a
 * translated line (404 = flag off, 403 = not the customer's dealer); every
 * other failure shows the generic text plus the API's message when it has
 * one (the withToasts convention).
 */
export function attachErrorText(error: unknown, t: Translate): string {
  const reason = attachBlockedReason(error);
  if (reason === "unavailable") return t("customerUpdate.logosAttachUnavailable");
  if (reason === "forbidden") return t("customerUpdate.logosAttachForbidden");
  const generic = t("customerUpdate.logosAddFailed");
  const message = (error as { message?: unknown } | undefined)?.message;
  return typeof message === "string" && message
    ? `${generic}: ${message}`
    : generic;
}

/**
 * Item number shown for a logo row: the product SKU, except the synthetic
 * UPLOAD_ skus of uploaded images (the logo list shows "-" for those too).
 */
export function logoItemNumber(sku: string | undefined): string {
  if (!sku || sku.startsWith("UPLOAD_")) return "-";
  return sku;
}

/**
 * The search rows worth offering: a global logo (isForAllCustomers) is
 * visible to every customer already and carries no vendor link, so the API
 * would only skip it (notVendorLogo) - it is dropped client-side instead of
 * being ticked and rejected after the POST. Rows without the flag (older
 * payloads) are kept; the API stays the judge at attach.
 */
export function attachableSearchResults(
  results: BaseLogo[] | undefined
): BaseLogo[] | undefined {
  return results?.filter((logo) => logo.isForAllCustomers !== true);
}

// ----- Logo tab v2 (flag REACT_APP_CUSTOMER_LOGOS_TAB_V2_ENABLED) -----

/** Chip per link source: tone + label key (+ tooltip key for 'linked'). */
export function linkSourceChip(source: CustomerLogoLinkSource): {
  tone: BadgeTone;
  key: string;
  hintKey?: string;
} {
  switch (source) {
    case "manual":
      return { tone: "blue", key: "customerUpdate.logosSourceManual" };
    case "assigned":
      return { tone: "blue", key: "customerUpdate.logosSourceAssigned" };
    case "customerUpload":
      return { tone: "green", key: "customerUpdate.logosSourceCustomerUpload" };
    case "nav":
      return { tone: "gray", key: "customerUpdate.logosSourceNav" };
    case "derived":
      return { tone: "gray", key: "customerUpdate.logosSourceDerived" };
    case "linked":
    default:
      return {
        tone: "gray",
        key: "customerUpdate.logosSourceLinked",
        hintKey: "customerUpdate.logosSourceLinkedHint",
      };
  }
}

/**
 * The confirm text before a DELETE: a marker link (manual/assigned) asks
 * today's question; a link of unknown source adds the warning that NAV's
 * customer filter may link it again at the nightly sync (Nichlas 12/9).
 */
export function removeConfirmKey(source: CustomerLogoLinkSource): string {
  return source === "linked"
    ? "customerUpdate.logosRemoveConfirmUnknown"
    : "customerUpdate.logosRemoveConfirm";
}

const NOT_REMOVABLE_I18N_KEYS: Record<string, string> = {
  customerUpload: "customerUpdate.logosNotRemovableCustomerUpload",
  navMainCustomer: "customerUpdate.logosNotRemovableNav",
  derivedChild: "customerUpdate.logosNotRemovableDerived",
};

/** i18n key of a notRemovableReason code; undefined for an unknown code. */
export function notRemovableReasonKey(
  reason: string | null | undefined
): string | undefined {
  return reason ? NOT_REMOVABLE_I18N_KEYS[reason] : undefined;
}

/**
 * Why a link cannot be removed, for the tooltip: the translated reason, an
 * unknown code as it is, or (no code at all) the source's own explanation.
 */
export function notRemovableText(link: CustomerLogoLink, t: Translate): string {
  const key = notRemovableReasonKey(link.notRemovableReason);
  if (key) return t(key);
  if (link.notRemovableReason) return link.notRemovableReason;
  const bySource = notRemovableReasonKey(
    link.source === "customerUpload"
      ? "customerUpload"
      : link.source === "nav"
        ? "navMainCustomer"
        : link.source === "derived"
          ? "derivedChild"
          : undefined
  );
  return bySource ? t(bySource) : t("customerUpdate.logosNotRemovable");
}

const LOGO_TYPE_I18N_KEYS: Partial<Record<string, string>> = {
  [LogoType.ImageLogo]: "customerUpdate.logosTypeImage",
  [LogoType.NameLogo]: "customerUpdate.logosTypeName",
  [LogoType.NumberLogo]: "customerUpdate.logosTypeNumber",
  [LogoType.TextLogo]: "customerUpdate.logosTypeText",
  [LogoType.SymbolLogo]: "customerUpdate.logosTypeSymbol",
};

/**
 * "Image" / "Name" / ... for the logo's class. LogoType.Unknown (a row
 * without a class) shows "—" like every other missing value - never the raw
 * enum word; a code this build does not know yet passes through as it is.
 */
export function logoTypeLabel(
  logoType: LogoType | string | undefined,
  t: Translate
): string {
  if (!logoType || logoType === LogoType.Unknown) return "—";
  const key = LOGO_TYPE_I18N_KEYS[logoType];
  return key ? t(key) : String(logoType);
}

/** Compact status chip via getLogoState (the logo list's state model). */
export function logoStateChip(logo: BaseLogo): { tone: BadgeTone; key: string } {
  switch (getLogoState(logo)) {
    case LogoState.Draft:
      return { tone: "orange", key: "compLogoDesign.statusDraft" };
    case LogoState.Approved:
      return { tone: "green", key: "compLogoDesign.statusApproved" };
    case LogoState.Rejected:
      return { tone: "red", key: "compLogoDesign.statusRejected" };
    case LogoState.Pending:
      return { tone: "gray", key: "compLogoDesign.statusAwaiting" };
    case LogoState.Undefined:
    default:
      return { tone: "gray", key: "compLogoDesign.statusUnknown" };
  }
}

/**
 * The colour column: the count plus the names for the tooltip - colorDetails
 * (names) when the row carries them, else the raw colour codes. Foreign-org
 * rows can carry neither: 0 colours, never a throw.
 */
export function logoColorsSummary(logo: BaseLogo): { count: number; names: string[] } {
  const names =
    logo.colorDetails && logo.colorDetails.length > 0
      ? logo.colorDetails.map((c) => c.name || c.id).filter((n) => !!n)
      : (logo.colors ?? []).filter((c) => !!c);
  return { count: names.length, names };
}

/**
 * "Open logo": the deep-link the order/design views use - the logo list
 * searched on the item number. An uploaded image (synthetic UPLOAD_ sku)
 * is searched on its serial or, failing that, its title; nothing at all
 * opens the plain list.
 */
export function logoOpenHref(logo: BaseLogo): string {
  const sku = logo.product?.sku;
  const term =
    sku && !sku.startsWith("UPLOAD_")
      ? sku
      : logo.serial || logo.title || "";
  return term
    ? `/logo?searchTerm=${encodeURIComponent(term)}&pageSize=25&pageIndex=1`
    : "/logo";
}

const HAS_ZONE = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

/**
 * A marker's createdUtc as a stamp the browser reads as UTC. The API writes
 * the marker with DateTime.UtcNow (serialised WITH 'Z') but reads it back
 * through EF as Kind=Unspecified (serialised WITHOUT a zone); a zone-less
 * stamp is parsed as LOCAL time, so the same marker would show another date
 * around midnight depending on whether the row is fresh or re-read. The
 * value is UTC by contract, so 'Z' is appended when no zone is there.
 */
export function utcStamp(stamp: string): string {
  const trimmed = stamp.trim();
  return HAS_ZONE.test(trimmed) ? trimmed : `${trimmed}Z`;
}

/**
 * The "Added" cell: date (+ " by <name>") of a marker link; undefined for
 * every other source (no marker = no reliable date - LogoDto.createdAtUtc is
 * the image's last write, not the link).
 */
export function linkAddedText(
  link: CustomerLogoLink | undefined,
  t: Translate
): string | undefined {
  if (!link || (link.source !== "manual" && link.source !== "assigned")) {
    return undefined;
  }
  if (!link.createdUtc) return undefined;
  const parsed = new Date(utcStamp(link.createdUtc));
  if (Number.isNaN(parsed.getTime())) return undefined;
  const date = formatDate(parsed);
  if (!date) return undefined;
  return link.createdByName
    ? t("customerUpdate.logosAddedBy", { date, name: link.createdByName })
    : date;
}

/**
 * The reason code of a DELETE's 409 { code: "notRemovable", reason }; undefined
 * for every other failure. errorStatusFromBody keeps such a body as the raw
 * JSON text in `message` (no `message` field, string `code`), so the envelope
 * is parsed here; a mapper that already lifted `reason` wins.
 */
export function detachNotRemovableReason(error: unknown): string | undefined {
  if (!(error instanceof DataError)) return undefined;
  const status = error.errorStatus;
  if (!status || status.code !== 409) return undefined;
  if (typeof status.reason === "string" && status.reason) return status.reason;
  const message = typeof status.message === "string" ? status.message : "";
  try {
    const body = JSON.parse(message) as { code?: unknown; reason?: unknown };
    if (body && typeof body === "object" && body.code === "notRemovable") {
      return typeof body.reason === "string" && body.reason ? body.reason : "";
    }
  } catch {
    // not the envelope
  }
  return undefined;
}

/**
 * Error toast of a failed DELETE: a 409 notRemovable names the API's reason
 * (translated when the code is known; an envelope without a reason gets the
 * generic "cannot be removed" line, never a raw code); everything else is the
 * generic text plus the API's message when it has one (the withToasts
 * convention).
 */
export function detachErrorText(error: unknown, t: Translate): string {
  const reason = detachNotRemovableReason(error);
  if (reason !== undefined) {
    const key = notRemovableReasonKey(reason);
    return t("customerUpdate.logosRemoveFailedNotRemovable", {
      reason: key ? t(key) : reason || t("customerUpdate.logosNotRemovable"),
    });
  }
  const generic = t("customerUpdate.logosRemoveFailed");
  const message = (error as { message?: unknown } | undefined)?.message;
  return typeof message === "string" && message
    ? `${generic}: ${message}`
    : generic;
}

/**
 * Whether the links are refetched (1 GET, the failure case only) after a
 * failed DELETE - when the API judged the link itself, so the row shows what
 * the API sees: 409 notRemovable (the source the API judged by) and 404 (the
 * link is gone already - removed by a colleague or the nightly sync, or the
 * API sub-flag is off and the link is no marker - so the stale chip and the
 * Remove button leave instead of inviting the same click again). Every other
 * failure (500, network) keeps the row as it is.
 */
export function shouldRefetchLinksAfterDetachFailure(error: unknown): boolean {
  if (detachNotRemovableReason(error) !== undefined) return true;
  return error instanceof DataError && error.errorStatus?.code === 404;
}
